import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtShort(v: number): string {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(1);
}

const MAX_BARS = 200;

export default function PnlPerTradeWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "trade", label: t.analytics.tradeLabel, align: "left" as const },
    { key: "date", label: t.analytics.dateLabel, align: "left" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
  ];
  const tableRows = (analyticsData?.sizingData ?? []).map((s) => ({
    trade: `#${s.tradeIndex + 1}`,
    date: new Date(s.date).toLocaleDateString(undefined),
    pnl: (
      <span className={s.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {s.pnl >= 0 ? "+" : ""}
        {s.pnl.toFixed(2)}
      </span>
    ),
  }));

  const built = useMemo(() => {
    if (!analyticsData || analyticsData.sizingData.length === 0) return null;

    // Down-sample to keep the chart legible (<= MAX_BARS bars).
    const all = analyticsData.sizingData;
    const step = Math.max(1, Math.ceil(all.length / MAX_BARS));
    const points = step > 1 ? all.filter((_, i) => i % step === 0) : all;

    const c = chartColors();
    const data = points.map((s) => Math.round(s.pnl * 100) / 100);
    const colors = points.map((s) => pnlColor(s.pnl));

    const options = {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 1, columnWidth: "85%", distributed: true },
      },
      xaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: number) => fmtShort(v) + "$",
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        x: { show: false },
        y: {
          formatter: (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`,
        },
      },
      colors,
      legend: { show: false },
      dataLabels: { enabled: false },
    };

    return { options, data };
  }, [analyticsData, isDark]);

  if (!analyticsData || analyticsData.sizingData.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.pnlPerTrade} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.pnlPerTrade}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 flex flex-col px-5 pb-5 pt-2">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : built ? (
          <LazyApexChart
            key={`pnl-per-trade-widget-${themeId}`}
            options={built.options}
            series={[{ name: t.analytics.pnlPerTrade, data: built.data }]}
            type="bar"
            height={240}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
